import { DotRezSessionBase } from "../dot-rez-session-base";
import {IDotRezUserSession} from "./dot-rez-user.session.interface";
import { ITripScheduleRequest } from "../../data-contracts/requests/booking/trip-schedule.request";
import { ITripScheduleResponse } from "../../data-contracts/booking/trips/trip-schedule.data-contract";
import { IServiceFactory } from "../../../service-factory.interface";
import { DotRezTransientSessionTimer } from "../session-timer/dot-rez-transient-session-timer";
import {
    IDotRezPerson, IDotRezPersonEmail,
    IDotRezPersonPhoneNumber,
    IDotRezPersonTravelDocument
} from "../../data-contracts/user/person.data-contracts";
import { IDotRezAddPhoneRequest } from "../../data-contracts/requests/user/add-phone.request";
import { IDotRezUpdatePersonDetailsRequest } from "../../data-contracts/requests/user/update-person-details.request";
import { IUpdateUserTravelDocumentRequest } from "../../../airline-webapi/requests/update-user-travel-document.request";
import { IAddUserTravelDocumentRequest } from "../../../airline-webapi/requests/add-user-travel-document.request";
import { Check } from "../../../../types/type-checking";
import {IDotRezRetrieveBookingByLastNameRequest} from "../../data-contracts/requests/booking/retrieve-booking-by-last-name.request";
import {IDotRezBookingData} from "../../data-contracts/booking/booking-state/booking-state.data-contracts";
import {findBookingByLastNameQuery} from "../../graph-ql/queries/booking-data-graphql.query";
import {BookingsReader} from "./bookings-reader";
import {IDotRezCurrencyConversionRequest} from "../../data-contracts/requests/user/currency-conversion.request";
import {ICurrencyConversionResponse} from "../../data-contracts/responses/user/currency-conversion.response";
import {IPersistedBookingHistory} from "../../../booking-history/models/persisted-booking-history.interface";
import {IDotRezUser, IDotRezUserAccountInfo} from "../../data-contracts/user/user-data-contracts";
import {DotRezResponse} from "../dot-rez-response";
import {BookingSessionCodesSettings} from "../booking-session/booking-session-codes-settings";
import {
    IDotRezBookingSessionCodesSettings
} from "../../data-contracts/responses/booking/booking-session-codes-settings";
import {IDotRezAddEmailAddressRequest} from "../../data-contracts/requests/user/add-email-address.request";
import {NullableString} from "../../../../types/nullable-types";


export class DotRezUserSession extends DotRezSessionBase implements IDotRezUserSession {
    constructor(token: string,
                services: IServiceFactory,
                private readonly _userKey: NullableString,
                private readonly _personKey: NullableString) {
        super(services, new DotRezTransientSessionTimer(token))
        this._pastBookingsReader = new BookingsReader(this);
    }

    private _pastBookingsReader: BookingsReader;

    async tripSchedule(request: ITripScheduleRequest): Promise<DotRezResponse<ITripScheduleResponse>> {
        return await this.get<ITripScheduleResponse>(`/api/nsk/v1/trip/schedule?Origin=${request.origin}&Destination=${request.destination}&BeginDate=${request.beginDate}&EndDate=${request.endDate}`, {
            doNotThrowOnError: true,
        });
    }

    private _getUserApiPath(path: string): string {
        const rootPath = `/api/nsk/v1/users/${this._userKey}`;

        return `${rootPath}${path}`;
    }

    async getCurrentUserPerson(): Promise<IDotRezPerson> {
        const response = await this.get<IDotRezPerson>(this._getUserApiPath('/person'));
        return response.data;
    }

    async getCurrentUser(): Promise<IDotRezUser> {

        const response = await this.get<IDotRezUser>(this._getUserApiPath(''));
        return response.data;
    }

    async updatePersonDetails(request: IDotRezUpdatePersonDetailsRequest): Promise<void> {
        await this.patch(this._getUserApiPath(`/person`), {
            body: request
        });
    }

    async addPhone(request: IDotRezAddPhoneRequest): Promise<void> {
        await this.post(this._getUserApiPath(`/person/phoneNumbers`), {
            body: request
        })
    }

    async updatePhone(request: IDotRezPersonPhoneNumber): Promise<void> {
        const { personPhoneNumberKey, ...body } = request;
        await this.patch(this._getUserApiPath(`/person/phoneNumbers/${personPhoneNumberKey}`), {
            body: body
        });
    }

    async addEmailAddress(request: IDotRezAddEmailAddressRequest): Promise<void> {
        await this.post(this._getUserApiPath(`/person/emails`), {
            body: request
        })
    }
    async updateEmailAddress(request: IDotRezPersonEmail): Promise<void> {
        await this.patch(this._getUserApiPath(`/person/emails/${request.personEmailKey}`), {
            body: {
                default: request.default,
                email: request.email
            }
        })
    }

    async deleteTravelDocument(travelDocumentKey: string): Promise<void> {
        await this.delete(this._getUserApiPath(`/person/travelDocuments/${travelDocumentKey}`));
    }

    async updateTravelDocument(request: IUpdateUserTravelDocumentRequest): Promise<IDotRezPersonTravelDocument> {
        const { travelDocumentKey, ...body } = request;
        await this.deleteTravelDocument(travelDocumentKey);
        return await this.addTravelDocument(body);
    }


    async addTravelDocument(request: IAddUserTravelDocumentRequest): Promise<IDotRezPersonTravelDocument> {
        const response = await this.post<IDotRezPersonTravelDocument>(this._getUserApiPath(`/person/travelDocuments`), {
            body: request
        });

        const url = response.getLocationHeader();
        if (Check.isNullOrUndefined(url)) {
            throw new Error(`url cannot be null or undefined`);
        }
        const newTravelDocument = await this.get<IDotRezPersonTravelDocument>(url);
        return newTravelDocument.data;
    }


    async findBookingByLastName(request: IDotRezRetrieveBookingByLastNameRequest): Promise<IDotRezBookingData> {
        const response = await this.executeGraphQLQuery<{bookingsv2: IDotRezBookingData}>(findBookingByLastNameQuery(request));
        return response.data.bookingsv2;
    }

    async getBookingsHistory(): Promise<IPersistedBookingHistory[]> {
        return await this._pastBookingsReader.getBookings();
    }

    async convertCurrency(request: IDotRezCurrencyConversionRequest): Promise<ICurrencyConversionResponse> {
        const response = await this.get<ICurrencyConversionResponse>(`/api/nsk/v1/currency/converter?FromCurrencyCode=${request.fromCurrencyCode}&ToCurrencyCode=${request.toCurrencyCode}&Amount=${request.amount}`);
        return response.data;
    }

    async getAccountInfo(organizationCode: string): Promise<DotRezResponse<IDotRezUserAccountInfo>> {
        return await this.get<IDotRezUserAccountInfo>(`/api/nsk/v1/organizations2/${organizationCode}/account`, {
            doNotThrowOnError: true
        });
    }

    async getCodesSettings(): Promise<BookingSessionCodesSettings> {
        const response = await this.get<IDotRezBookingSessionCodesSettings>('/api/nsk/v1/settings/general/codes');
        return new BookingSessionCodesSettings(response.data);
    }

}
